import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Cookie, ChevronLeft } from 'lucide-react';
import { openConsentBanner } from '../components/ConsentBanner';

const CookieRow = ({ name, vendor, purpose, duration, policyHref }) => (
  <tr className="border-b border-apple-700/20 align-top">
    <td className="py-2 pr-4 text-apple-100 text-sm font-medium whitespace-nowrap">{name}</td>
    <td className="py-2 pr-4 text-apple-300 text-sm">{vendor}</td>
    <td className="py-2 pr-4 text-apple-300 text-sm">{purpose}</td>
    <td className="py-2 pr-4 text-apple-300 text-sm whitespace-nowrap">{duration}</td>
    <td className="py-2 text-sm">
      {policyHref ? (
        <a href={policyHref} target="_blank" rel="noopener noreferrer" className="text-sortio-primary hover:text-sortio-primary/80 underline">
          Vendor policy
        </a>
      ) : (
        <span className="text-apple-400">N/A</span>
      )}
    </td>
  </tr>
);

const CookieTable = ({ title, intro, rows }) => (
  <section className="space-y-3">
    <h3 className="text-lg font-medium text-apple-50">{title}</h3>
    {intro && <p className="text-apple-300 text-sm">{intro}</p>}
    <div className="overflow-x-auto">
      <table className="w-full text-left">
        <thead>
          <tr className="border-b border-apple-600/40 text-apple-400 text-xs uppercase tracking-wider">
            <th className="py-2 pr-4">Name</th>
            <th className="py-2 pr-4">Vendor</th>
            <th className="py-2 pr-4">Purpose</th>
            <th className="py-2 pr-4">Duration</th>
            <th className="py-2">Policy</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <CookieRow key={i} {...r} />
          ))}
        </tbody>
      </table>
    </div>
  </section>
);

const CookiePolicyPage = () => {
  return (
    <div className="min-h-screen bg-black text-apple-100 py-24">
      <Helmet>
        <title>Cookie Policy - Sortio</title>
        <meta name="description" content="Cookie Policy for Sortio: per-cookie inventory across strictly necessary, analytics, and marketing categories." />
      </Helmet>

      <div className="absolute top-0 inset-x-0 h-[500px] bg-gradient-to-b from-apple-700/10 to-transparent"></div>

      <div className="container mx-auto px-6 max-w-4xl relative z-10">
        <Link to="/" className="inline-flex items-center text-apple-300 hover:text-sortio-primary mb-12 transition-colors group">
          <ChevronLeft className="w-4 h-4 mr-1 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Home</span>
        </Link>

        <div className="mb-16 flex flex-col items-center">
          <div className="w-14 h-14 rounded-full bg-apple-700/60 backdrop-blur-sm flex items-center justify-center mb-6 border border-apple-600/30">
            <Cookie className="w-6 h-6 text-sortio-primary" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-center">
            Cookie <span className="apple-gradient-text">Policy</span>
          </h1>
          <p className="text-apple-300 mt-4 text-center max-w-xl">
            How we use cookies and similar storage on getsortio.com.
          </p>
        </div>

        <div className="apple-panel p-8 md:p-12 rounded-apple backdrop-blur-sm mb-12">
          <div className="space-y-10 text-apple-300 leading-relaxed">
            <p className="text-sm text-apple-400"><em>Last updated: 16 May 2026</em></p>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">How we use cookies</h2>
              <p>
                We use three categories of cookies on this website. Strictly necessary cookies are always active. Analytics and marketing cookies load only after you opt in through the Cookie Settings banner. You can change your choices at any time by clicking{' '}
                <button
                  type="button"
                  onClick={openConsentBanner}
                  className="text-sortio-primary hover:text-sortio-primary/80 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sortio-primary rounded"
                >
                  Cookie Settings
                </button>
                {' '}or visiting <Link to="/privacy/choices" className="text-sortio-primary hover:text-sortio-primary/80">Your Privacy Choices</Link>. Until you opt in, no third-party analytics or advertising scripts load on this site (we use Google Consent Mode v2 in default-denied state).
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Cookieless visit counting</h2>
              <p>
                Separately from the cookie categories below, we count page views and download clicks
                ourselves, on our own servers, for every visit. This runs whether or not you opt in,
                because it does not use cookies and does not store or read anything on your device,
                so it falls outside the consent rules that govern cookies.
              </p>
              <p>
                It records the page you viewed, the site that referred you (domain only, never the
                full address or any search terms), your country, and your device and operating system
                type. <strong className="text-apple-100">We do not store your IP address</strong>, and
                we set no identifier that can follow you between days or between sites. To recognise
                repeat visits within a single day we derive a one-way, randomly salted code from your
                IP address and browser; the salt changes every day, which means yesterday's records
                cannot be linked to today's, by us or by anyone else. There is no session recording,
                no profiling, and none of this data is shared with or sold to anyone.
              </p>
              <p>
                If you create a Sortio account on the same day you first visited, we use that
                same-day code once to record which website referred you, so we can tell which
                channels bring people to Sortio. Only the referring domain is saved to your account
                (for example "google.com", or "direct" if you came straight to us). The code itself
                is not saved, your IP address is not saved, and because the code changes daily this
                never reaches back to earlier visits.
              </p>
            </section>

            <CookieTable
              title="Strictly necessary (always on)"
              intro="Required to operate the site, authenticate users, prevent fraud, and remember your consent choice. These cookies cannot be disabled."
              rows={[
                {
                  name: 'sortio_consent_v1',
                  vendor: 'Sortio',
                  purpose: 'Stores your cookie consent choice (analytics, marketing, preferences) plus a small hash.',
                  duration: 'Persistent (until cleared)',
                  policyHref: '/cookie-policy',
                },
                {
                  name: 'sortio_attribution',
                  vendor: 'Sortio',
                  purpose: 'Captures ad-click context (utm_*, gclid, fbclid) so we can attribute downloads correctly during signup.',
                  duration: '30 days',
                  policyHref: '/privacy-policy',
                },
                {
                  name: '__stripe_mid / __stripe_sid',
                  vendor: 'Stripe',
                  purpose: 'Fraud prevention on the checkout flow.',
                  duration: 'Session and up to 1 year',
                  policyHref: 'https://stripe.com/cookie-settings',
                },
              ]}
            />

            <CookieTable
              title="Analytics (loads only with your opt-in)"
              intro="Helps us understand how the site is used and where users get stuck. Disabled by default."
              rows={[
                {
                  name: '_ga / _ga_*',
                  vendor: 'Google Analytics 4',
                  purpose: 'Distinguishes unique users and sessions; aggregates traffic metrics.',
                  duration: 'Up to 2 years',
                  policyHref: 'https://policies.google.com/privacy',
                },
                {
                  name: '_clck / _clsk and related',
                  vendor: 'Microsoft Clarity',
                  purpose: 'Session-replay tool that records anonymized pointer movement, clicks, and scrolling. Used to identify UX issues. Replays are sampled and exclude form field inputs and content marked as sensitive.',
                  duration: 'Up to 1 year',
                  policyHref: 'https://privacy.microsoft.com/privacystatement',
                },
                {
                  name: '_vercel_jwt and related',
                  vendor: 'Vercel Analytics and Speed Insights',
                  purpose: 'Page-view counting and Core Web Vitals measurement. Privacy-friendly, no cross-site tracking.',
                  duration: 'Session',
                  policyHref: 'https://vercel.com/legal/privacy-policy',
                },
              ]}
            />

            <CookieTable
              title="Marketing (loads only with your opt-in)"
              intro="Lets us measure ad performance and reward affiliates. Disabled by default."
              rows={[
                {
                  name: '_fbp / fr',
                  vendor: 'Meta Pixel',
                  purpose: 'Attribution for advertising campaigns on Facebook and Instagram; deduplication with server-side Conversions API.',
                  duration: 'Up to 90 days',
                  policyHref: 'https://www.facebook.com/policy.php',
                },
                {
                  name: 'rewardful.referral',
                  vendor: 'Rewardful',
                  purpose: 'Affiliate tracking so referrers get credit for signups they drive.',
                  duration: '60 days',
                  policyHref: 'https://www.rewardful.com/privacy',
                },
                {
                  name: '_gcl_au',
                  vendor: 'Google Ads (via GA4 link)',
                  purpose: 'Conversion measurement for Google Ads campaigns. Only fires after you grant marketing consent.',
                  duration: '90 days',
                  policyHref: 'https://policies.google.com/privacy',
                },
              ]}
            />

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">A note on session replay (Microsoft Clarity)</h2>
              <p>
                Microsoft Clarity is a session-replay tool. When enabled by your consent, it records mouse movement, clicks, scrolls, and the rendered page so we can review aggregate UX issues. Clarity automatically masks form inputs by default and we configure it to redact content marked as sensitive. We do not use replays to identify individual users; the goal is to find broken pages and confusing flows. You can opt out at any time via the Cookie Settings banner or your browser's privacy controls.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Desktop app</h2>
              <p>
                Sortio's desktop application stores local settings on your device and does not set website cookies. Optional diagnostics and telemetry inside the app are off by default for EEA and UK users and require your opt-in in <strong>Settings, Privacy</strong>.
              </p>
            </section>

            <section className="space-y-3">
              <h2 className="text-xl font-medium text-apple-50">Managing your choices</h2>
              <p>
                Open <button
                  type="button"
                  onClick={openConsentBanner}
                  className="text-sortio-primary hover:text-sortio-primary/80 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-sortio-primary rounded"
                >
                  Cookie Settings
                </button> at any time, or visit <Link to="/privacy/choices" className="text-sortio-primary hover:text-sortio-primary/80">Your Privacy Choices</Link> for the US state opt-out form. We honor <strong>Global Privacy Control (GPC)</strong> signals automatically.
              </p>
              <p>
                For questions, contact us at{' '}
                <a href="mailto:marcus@getsortio.com" className="text-sortio-primary hover:text-sortio-primary/80">marcus@getsortio.com</a>.
              </p>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookiePolicyPage;
